import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Modal,
  Button,
  Form,
  Table,
  Badge,
  Card,
  Row,
  Col,
  InputGroup,
  Spinner,
} from "react-bootstrap";
import {
  FiSearch,
  FiUsers,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiSend,
  FiEdit,
  FiPlus,
} from "react-icons/fi";
import { FaSort } from "react-icons/fa";
import Select from "react-select";
import { selectStyle } from "../../../services/constant";
import {
  callToast,
  showErrorMessages,
} from "../../../services/utilityFunctions.js";
import { getLocalStorageData } from "../../../services/axiosComponent";

const GenericApprovalWorkflow = ({
  entity,
  entityId,
  entityType,
  navigate,
  onApprovalSent,
  // Service function props
  getApproversList,
  getApprovalRequestList,
  getApprovalRequestDetails,
  addApprovalRequest,

  updateApprovalRequest,
  // Optional customization props
  approvalButtonText = "Send for Approval",
  modalTitle = "Send for Approval",
  isDraftField = "STATUS", // Field to check for draft status
  draftValues = ["Draft", "DRAFT"], // Values that indicate draft status
}) => {
  // State for modal and form
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [formData, setFormData] = useState({
    comment: "",
    approverIds: [],
  });
  const [editFormData, setEditFormData] = useState({
    comment: "",
    approverIds: [],
  });
  const [errors, setErrors] = useState({});
  const [editErrors, setEditErrors] = useState({});

  // State for approvers dropdown
  const [approverOptions, setApproverOptions] = useState([]);
  const [isApproversLoading, setIsApproversLoading] = useState(false);

  // State for approval details (when status is not DRAFT)
  const [approvalDetails, setApprovalDetails] = useState(null);
  const [approvalSummary, setSummary] = useState({
    total: 0,
    accepted: 0,
    rejected: 0,
    pending: 0,
  });
  const [assignees, setAssignees] = useState([]);
  const [filteredAssignees, setFilteredAssignees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  // Check if entity is in draft status
  const isDraft = useMemo(() => {
    if (!entity || !isDraftField) return false;
    const fieldValue = entity[isDraftField];
    return draftValues.includes(fieldValue);
  }, [entity, isDraftField, draftValues]);

  // Fetch approvers for the dropdown
  const fetchApprovers = useCallback(async () => {
    if (!getApproversList) {
      console.error("getApproversList function not provided");
      return;
    }

    setIsApproversLoading(true);
    try {
      const response = await getApproversList({
        status: "Y",
        organizationId:
          getLocalStorageData("profileDetails")["_ORGANIZATION_"]._ID,
          type:entityType,
      });

      if (response.status === "success") {
        const options = response.data.entityList.map((approver) => ({
          value: approver["_ID"],
          label: `${
            approver.USER || approver.NAME || approver.FIRST_NAME || "Unknown"
          }`,
          name:
            approver.USER || approver.NAME || approver.FIRST_NAME || "Unknown",
          username:
            approver.USER || approver.USER_CODE || approver.USERNAME || "",
        }));
        setApproverOptions(options);
      } else {
        showErrorMessages({ data: response, navigate });
      }
    } catch (error) {
      console.error("Error fetching approvers:", error);
      showErrorMessages({
        data: { message: "Failed to fetch approvers" },
        navigate,
      });
    }
    setIsApproversLoading(false);
  }, [getApproversList, navigate]);

  // Fetch approval request details for non-draft entities
  const fetchApprovalDetails = useCallback(async () => {
    if (isDraft || !getApprovalRequestList || !getApprovalRequestDetails) {
      console.error(
        "Required functions not provided for fetching approval details"
      );
      return;
    }

    setDetailsLoading(true);
    try {
      // First, get the approval request list to find the request for this entity
      const requestListResponse = await getApprovalRequestList({
        entityId: entityId,
      });

      if (
        requestListResponse.status === "success" &&
        requestListResponse.data.entityList.length > 0
      ) {
        // Find the approval request for this entity
        const approvalRequest = requestListResponse.data.entityList.find(
          (item) => item._ENTITY === entityId
        );

        if (approvalRequest) {
          // Get detailed approval information
          const detailsResponse = await getApprovalRequestDetails(
            approvalRequest._ID
          );

          if (detailsResponse.status === "success") {
            const details = detailsResponse.data;
            setApprovalDetails(details);

            // Extract assignees and calculate summary
            const assigneesList = details._ASSIGNEES || [];
            setAssignees(assigneesList);
            setFilteredAssignees(assigneesList);

            // Calculate summary statistics
            const summary = assigneesList.reduce(
              (acc, assignee) => {
                acc.total++;
                switch (assignee.STATUS?.toUpperCase()) {
                  case "ACCEPTED":
                  case "APPROVED":
                    acc.accepted++;
                    break;
                  case "REJECTED":
                    acc.rejected++;
                    break;
                  default:
                    acc.pending++;
                }
                return acc;
              },
              { total: 0, accepted: 0, rejected: 0, pending: 0 }
            );

            setSummary(summary);
          } else {
            showErrorMessages({ data: detailsResponse, navigate });
          }
        }
      }
    } catch (error) {
      console.error("Error fetching approval details:", error);
      showErrorMessages({
        data: { message: "Failed to fetch approval details" },
        navigate,
      });
    }
    setDetailsLoading(false);
  }, [
    isDraft,
    entityId,
    navigate,
    getApprovalRequestList,
    getApprovalRequestDetails,
  ]);

  // Initialize component
  useEffect(() => {
    if (isDraft) {
      fetchApprovers();
    } else {
      fetchApprovers(); // Also fetch approvers for edit functionality
      fetchApprovalDetails();
    }
  }, [isDraft, fetchApprovers, fetchApprovalDetails]);

  // Handle form input changes
  const handleInputChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  }, []);

  // Handle edit form input changes
  const handleEditInputChange = useCallback((field, value) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
    setEditErrors((prev) => ({ ...prev, [field]: "" }));
  }, []);

  // Validate form
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.comment.trim()) {
      newErrors.comment = "Comment is required";
    }

    if (formData.approverIds.length === 0) {
      newErrors.approverIds = "At least one approver must be selected";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Validate edit form
  const validateEditForm = useCallback(() => {
    const newErrors = {};

    if (!editFormData.comment.trim()) {
      newErrors.comment = "Comment is required";
    }

    if (editFormData.approverIds.length === 0) {
      newErrors.approverIds = "At least one approver must be selected";
    }

    setEditErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [editFormData]);

  // Submit approval request
  const handleSubmitApproval = useCallback(async () => {
    if (!validateForm() || !addApprovalRequest) {
      if (!addApprovalRequest) {
        console.error("addApprovalRequest function not provided");
      }
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        comment: formData.comment,
        type: entityType,
        entityId: entityId,
        approverIds: formData.approverIds.map((approver) => approver.value),
        organizationId:
          getLocalStorageData("profileDetails")["_ORGANIZATION_"]._ID,
      };

      const response = await addApprovalRequest(submitData);

      if (response.status === "success") {
        callToast(
          "success",
          "Success",
          "Entity sent for approval successfully"
        );
        setShowModal(false);
        setFormData({ comment: "", approverIds: [] });

        // Call callback if provided
        if (onApprovalSent) {
          onApprovalSent();
        }
      } else {
        showErrorMessages({ data: response, navigate });
      }
    } catch (error) {
      console.error("Error submitting approval request:", error);
      showErrorMessages({
        data: { message: "Failed to submit approval request" },
        navigate,
      });
    }
    setLoading(false);
  }, [
    validateForm,
    formData,
    entityType,
    entityId,
    navigate,
    onApprovalSent,
    addApprovalRequest,
  ]);

  // Update approval request
  const handleUpdateApproval = useCallback(async () => {
    if (!validateEditForm() || !updateApprovalRequest || !approvalDetails) {
      if (!updateApprovalRequest) {
        console.error("updateApprovalRequest function not provided");
      }
      return;
    }

    setEditLoading(true);
    try {
      console.log("Submitting edit approval request with data:", editFormData);
      console.log("Approval details:", approvalDetails);
      const submitData = {
        comment: editFormData.comment,
        approverIds: editFormData.approverIds.map((approver) => approver.value),
        entityId: entityId,
        id: approvalDetails._ID,
        type: entityType,
        organizationId:
          getLocalStorageData("profileDetails")["_ORGANIZATION_"]._ID,
      };

      const response = await updateApprovalRequest(
        submitData,
        approvalDetails._ID
      );

      if (response.status === "success") {
        callToast(
          "success",
          "Success",
          "Approval request updated successfully"
        );
        setShowEditModal(false);
        setEditFormData({ comment: "", approverIds: [] });

        // Refresh approval details
        fetchApprovalDetails();

        // Call callback if provided
        if (onApprovalSent) {
          onApprovalSent();
        }
      } else {
        showErrorMessages({ data: response, navigate });
      }
    } catch (error) {
      console.error("Error updating approval request:", error);
      showErrorMessages({
        data: { message: "Failed to update approval request" },
        navigate,
      });
    }
    setEditLoading(false);
  }, [
    validateEditForm,
    editFormData,
    approvalDetails,
    navigate,
    onApprovalSent,
    updateApprovalRequest,
    fetchApprovalDetails,
  ]);

  // Open edit modal with pre-filled data
  const handleOpenEditModal = useCallback(() => {
    if (!approvalDetails || !assignees) return;

    // Pre-fill with current approvers
    const currentApprovers = assignees.map((assignee) => ({
      value: assignee["_APPROVER"],
      label: assignee.USER || assignee.NAME || "Unknown",
      name: assignee.USER || assignee.NAME || "Unknown",
      username: assignee["USER CODE"] || "",
    }));

    setEditFormData({
      comment: approvalDetails._COMMENT || "",
      approverIds: currentApprovers,
    });
    setEditErrors({});
    setShowEditModal(true);
  }, [approvalDetails, assignees]);

  // Handle search with loading simulation (like Series Tab)
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value.trim()) {
      setSearchLoading(true);
      // Simulate search delay like in SeriesTab
      setTimeout(() => {
        setSearchLoading(false);
      }, 300);
    } else {
      setSearchLoading(false);
    }
  };

  // Filter assignees based on search and status
  useEffect(() => {
    let filtered = assignees;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (assignee) =>
          assignee.USER?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          assignee["USER CODE"]
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== "ALL") {
      filtered = filtered.filter(
        (assignee) => assignee.STATUS?.toUpperCase() === statusFilter
      );
    }

    setFilteredAssignees(filtered);
  }, [assignees, searchTerm, statusFilter]);

  const StatusBadge = useMemo(() => {
    return ({ status }) => {
      const statusUpper = status?.toUpperCase();
      let variant, icon, text;

      switch (statusUpper) {
        case "ACCEPTED":
        case "APPROVED":
          variant = "success";
          icon = <FiCheckCircle />;
          text = "Approved";
          break;
        case "REJECTED":
          variant = "danger";
          icon = <FiXCircle />;
          text = "Rejected";
          break;
        default:
          variant = "warning";
          icon = <FiClock />;
          text = "Pending";
      }

      return (
        <Badge
          bg={variant}
          className="d-inline-flex align-items-center justify-content-center gap-1"
          style={{
            fontSize: "0.75rem",
            fontWeight: "500",
            padding: "4px 8px",
            borderRadius: "6px",
            minWidth: "80px",
            textAlign: "center",
          }}
        >
          {icon}
          <span>{text}</span>
        </Badge>
      );
    };
  }, []);

  // Get status badge variant helper
  const getStatusBadgeVariant = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "warning";
      case "approved":
        return "success";
      case "rejected":
        return "danger";
      default:
        return "secondary";
    }
  };

  // Table Header Cell component (similar to SeriesTab)
  const TableHeaderCell = ({ columnName }) => (
    <th style={{ verticalAlign: "middle" }}>
      {columnName}{" "}
      <FaSort
        style={{
          cursor: "pointer",
          transition: "transform 0.2s ease",
        }}
        className="hover-scale"
      />
    </th>
  );

  // Render approval request form modal
  const renderApprovalModal = () => (
    <Modal
      show={showModal}
      onHide={() => setShowModal(false)}
      size="lg"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <FiSend className="me-2" />
          {modalTitle}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Comment *</Form.Label>
            <Form.Control
              as="textarea"
              rows={1}
              placeholder="Enter approval comment..."
              value={formData.comment}
              onChange={(e) => handleInputChange("comment", e.target.value)}
              isInvalid={!!errors.comment}
            />
            <Form.Control.Feedback type="invalid">
              {errors.comment}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Select Approvers *</Form.Label>
            <Select
              isMulti
              options={approverOptions}
              value={formData.approverIds}
              onChange={(selected) =>
                handleInputChange("approverIds", selected || [])
              }
              styles={selectStyle}
              placeholder="Select approvers..."
              isLoading={isApproversLoading}
              isSearchable
              className={errors.approverIds ? "is-invalid" : ""}
            />
            {errors.approverIds && (
              <div className="invalid-feedback d-block">
                {errors.approverIds}
              </div>
            )}
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowModal(false)}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmitApproval}
          disabled={loading}
        >
          {loading ? (
            <div
              className="spinner-border spinner-border-sm me-2"
              role="status"
            >
              <span className="visually-hidden">Loading...</span>
            </div>
          ) : null}
          Send for Approval
        </Button>
      </Modal.Footer>
    </Modal>
  );

  // Render edit approval request modal
  const renderEditModal = () => (
    <Modal
      centered
      show={showEditModal}
      onHide={() => setShowEditModal(false)}
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <FiEdit className="me-2" />
          Edit Approval Request
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Comment *</Form.Label>
            <Form.Control
              as="textarea"
              rows={1}
              placeholder="Enter approval comment..."
              value={editFormData.comment}
              onChange={(e) => handleEditInputChange("comment", e.target.value)}
              isInvalid={!!editErrors.comment}
            />
            <Form.Control.Feedback type="invalid">
              {editErrors.comment}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Select Approvers *</Form.Label>
            <Select
              isMulti
              options={approverOptions}
              value={editFormData.approverIds}
              onChange={(selected) =>
                handleEditInputChange("approverIds", selected || [])
              }
              styles={selectStyle}
              placeholder="Select approvers..."
              isLoading={isApproversLoading}
              isSearchable
              className={editErrors.approverIds ? "is-invalid" : ""}
            />
            {editErrors.approverIds && (
              <div className="invalid-feedback d-block">
                {editErrors.approverIds}
              </div>
            )}
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowEditModal(false)}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleUpdateApproval}
          disabled={editLoading}
        >
          {editLoading ? (
            <div
              className="spinner-border spinner-border-sm me-2"
              role="status"
            >
              <span className="visually-hidden">Loading...</span>
            </div>
          ) : null}
          Update Approval Request
        </Button>
      </Modal.Footer>
    </Modal>
  );

  // Main render for draft status (simplified button like SeriesTab)
  if (isDraft) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-4 text-center">
          <div className="mb-3">
            <FiSend size={32} className="text-primary mb-2" />
            <h5 className="mb-2">Ready for Approval</h5>
            <p className="text-muted mb-0">
              This {entityType.toLowerCase().replace("_", " ")} is currently in
              draft status. Send it for approval to proceed with the review
              process.
            </p>
          </div>

          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowModal(true)}
            className="d-flex align-items-center justify-content-center hover-scale mx-auto"
            disabled={!getApproversList || !addApprovalRequest}
            style={{
              minWidth: "180px",
              padding: "8px 16px",
              fontWeight: "500",
            }}
          >
            <FiSend className="me-2" size={14} />
            {approvalButtonText}
          </Button>

          {(!getApproversList || !addApprovalRequest) && (
            <small className="text-muted d-block mt-2">
              Approval functionality is currently unavailable
            </small>
          )}
        </Card.Body>
        {renderApprovalModal()}
      </Card>
    );
  }

  // Main render for non-draft status (styled like SeriesTab)
  return (
    <Card className="shadow-sm m-0 p-0">
      <Card.Header className="pb-3">
        {/* Header section matching SeriesTab style */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2 slide-in">
          <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-2">
            <h4 className="mb-1 mb-sm-0">Approval Status</h4>
            {assignees.length > 0 && (
              <Badge
                bg="primary"
                className="align-self-start align-self-sm-center"
                style={{
                  fontSize: "0.75rem",
                  padding: "4px 8px",
                  borderRadius: "12px",
                  fontWeight: "500",
                }}
              >
                {assignees.length}
              </Badge>
            )}
            {approvalDetails && (
              <Badge
                bg={getStatusBadgeVariant(approvalDetails.STATUS)}
                style={{
                  fontSize: "0.75rem",
                  fontWeight: "500",
                  padding: "6px 10px",
                  borderRadius: "6px",
                }}
              >
                {approvalDetails.STATUS || "Unknown"}
              </Badge>
            )}
          </div>

          <div className="d-flex flex-column flex-md-row align-items-end align-items-md-center w-md-auto gap-2">
            <div className="d-flex flex-wrap justify-content-end align-items-center gap-2">
              {approvalDetails?.STATUS?.toLowerCase() === "pending" &&
                updateApprovalRequest && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleOpenEditModal}
                    className="hover-scale"
                    style={{ fontSize: "0.8rem" }}
                  >
                    <FiEdit className="me-1" size={14} />
                    Edit
                  </Button>
                )}
            </div>

            <div className="d-flex gap-2 align-items-center">
              <Form.Select
                size="sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  borderColor: "#dee2e6",
                  minWidth: "120px",
                }}
              >
                <option value="ALL">All Status</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
                <option value="PENDING">Pending</option>
              </Form.Select>

              <InputGroup
                style={{
                  minWidth: "250px",
                  maxWidth: "300px",
                  border: "1px solid #dee2e6",
                  borderRadius: "0.375rem",
                  overflow: "hidden",
                  transition: "all 0.2s ease",
                }}
              >
                <InputGroup.Text
                  className={`bg-transparent border-0 ${
                    searchLoading ? "pe-2" : "pe-1"
                  }`}
                  style={{
                    transition: "all 0.2s ease",
                  }}
                >
                  {searchLoading ? (
                    <Spinner
                      animation="border"
                      role="status"
                      size="sm"
                      className="text-primary"
                      style={{ width: "1.2rem", height: "1.2rem" }}
                    />
                  ) : (
                    <FiSearch className="text-muted" />
                  )}
                </InputGroup.Text>

                <Form.Control
                  size="sm"
                  type="text"
                  placeholder="Search "
                  value={searchTerm}
                  onChange={handleSearch}
                  className="border-0 ps-1"
                  style={{
                    transition: "all 0.2s ease",
                    outline: "none",
                    border: "none !important",
                    boxShadow: "none",
                  }}
                  onFocus={(e) => {
                    e.target.parentElement.style.borderColor = "#86b7fe";
                    e.target.parentElement.style.boxShadow =
                      "0 0 0 0.2rem rgba(13, 110, 253, 0.25)";
                  }}
                  onBlur={(e) => {
                    e.target.parentElement.style.borderColor = "#dee2e6";
                    e.target.parentElement.style.boxShadow = "none";
                  }}
                />
              </InputGroup>
            </div>
          </div>
        </div>

        {/* Status filter matching SeriesTab style */}

        {/* Summary Cards - Smaller and more compact */}
        <Row className="mb-3 g-3">
          <Col sm={6} lg={3}>
            <div
              className="text-center p-2 border rounded"
              style={{
                backgroundColor: "#ffffff",
                borderColor: "#dee2e6",
              }}
            >
              <h6 className="mb-1 text-dark">{approvalSummary.total}</h6>
              <small className="text-muted fw-medium">Total</small>
            </div>
          </Col>
          <Col sm={6} lg={3}>
            <div
              className="text-center p-2 border rounded"
              style={{
                backgroundColor: "#ffffff",
                borderColor: "#dee2e6",
              }}
            >
              <h6 className="mb-1 text-success">{approvalSummary.accepted}</h6>
              <small className="text-muted fw-medium">Approved</small>
            </div>
          </Col>
          <Col sm={6} lg={3}>
            <div
              className="text-center p-2 border rounded"
              style={{
                backgroundColor: "#ffffff",
                borderColor: "#dee2e6",
              }}
            >
              <h6 className="mb-1 text-danger">{approvalSummary.rejected}</h6>
              <small className="text-muted fw-medium">Rejected</small>
            </div>
          </Col>
          <Col sm={6} lg={3}>
            <div
              className="text-center p-2 border rounded"
              style={{
                backgroundColor: "#ffffff",
                borderColor: "#dee2e6",
              }}
            >
              <h6 className="mb-1 text-warning">{approvalSummary.pending}</h6>
              <small className="text-muted fw-medium">Pending</small>
            </div>
          </Col>
        </Row>

        {/* Approval comment display */}
        {/* {approvalDetails?._COMMENT && (
          <div className="mb-3 p-2 bg-light rounded">
            <small className="text-muted fw-medium">Comment: </small>
            <span className="fst-italic">"{approvalDetails._COMMENT}"</span>
          </div>
        )} */}
      </Card.Header>

      <Card.Body className="pt-0 mt-0">
        {detailsLoading ? (
          <div className="text-center py-5 fade-in">
            <Spinner animation="border" role="status" className="text-primary">
              <span className="visually-hidden">
                Loading approval details...
              </span>
            </Spinner>
            <p className="text-muted mt-2">Loading approval details...</p>
          </div>
        ) : assignees.length === 0 ? (
          // Empty state
          <div className="text-center py-5 fade-in">
            <FiUsers size={48} className="text-muted mb-3" />
            <h6 className="text-muted mb-2">No Approval Request Found</h6>
            <p className="text-muted small mb-3">
              No approval request has been created for this entity yet.
            </p>
          </div>
        ) : (
          <div
            className="table-responsive border table-container fade-in"
            style={{
              minHeight: "30vh",
              maxHeight: "50vh",
              overflow: "auto",
              overflowX: "visible",
              borderRadius: "15px",
              boxShadow:
                "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            }}
          >
            <Table
              hover
              className="mb-0 table-fixed"
              style={{
                position: "relative",
                borderCollapse: "separate",
                borderSpacing: 0,
                overflow: "auto",
                borderRadius: "15px",
              }}
            >
              <thead
                style={{
                  position: "sticky",
                  top: 0,
                  background: "white",
                  zIndex: 2,
                  boxShadow: "0 2px 2px -1px rgba(0, 0, 0, 0.1)",
                }}
              >
                <tr
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "400px",
                    textAlign: "center",
                    verticalAlign: "middle",
                  }}
                >
                  <TableHeaderCell columnName="Name" />
                  <TableHeaderCell columnName="User Code" />
                  <TableHeaderCell columnName="Status" />
                </tr>
              </thead>
              <tbody>
                {filteredAssignees.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-center py-4 text-muted">
                      <FiSearch size={20} className="me-2" />
                      No approvers match your search criteria
                    </td>
                  </tr>
                ) : (
                  filteredAssignees.map((assignee, index) => (
                    <tr
                      key={assignee._ID || index}
                      className="table-row"
                      style={{
                        maxHeight: "40px",
                        animation: `fadeIn 0.3s ease-in-out ${
                          index * 0.05
                        }s both`,
                        transition: "background-color 0.2s ease",
                      }}
                    >
                      <td
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "400px",
                          textAlign: "left",
                          verticalAlign: "middle",
                          transition: "background-color 0.15s ease",
                          padding: "12px 16px",
                          color: "#495057",
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <FiUsers className="me-2 text-primary" size={14} />
                          {assignee.USER || "-"}
                        </div>
                      </td>
                      <td
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "400px",
                          textAlign: "center",
                          verticalAlign: "middle",
                          transition: "background-color 0.15s ease",
                          padding: "12px 16px",
                          color: "#6c757d",
                        }}
                      >
                        {assignee["USER CODE"] || "-"}
                      </td>
                      <td
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "400px",
                          textAlign: "center",
                          verticalAlign: "middle",
                          transition: "background-color 0.15s ease",
                          padding: "12px 16px",
                        }}
                      >
                        <StatusBadge status={assignee.STATUS} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        )}

        {/* Footer with pagination-style info matching SeriesTab */}
        {assignees.length > 0 && (
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-stretch mt-3 gap-2">
            <div className="d-flex justify-content-between justify-content-md-start align-items-center gap-3 flex-grow-1">
              <div className="text-nowrap">
                Showing {filteredAssignees.length} of {assignees.length}{" "}
                approvers
                {searchTerm && ` matching "${searchTerm}"`}
              </div>
            </div>
          </div>
        )}

        {renderEditModal()}
      </Card.Body>

      {/* Add CSS animations matching SeriesTab */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .fade-in {
          animation: fadeIn 0.3s ease-in-out;
        }

        .slide-in {
          animation: slideIn 0.3s ease-out;
        }

        @keyframes slideIn {
          from {
            transform: translateY(-10px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .hover-scale {
          transition: transform 0.15s ease-in-out;
        }

        .hover-scale:hover {
          transform: scale(1.02);
        }

        .table-row {
          transition: background-color 0.2s ease;
        }

        .table-row:hover {
          background-color: rgba(13, 110, 253, 0.04);
        }

        .table-container {
          scroll-behavior: smooth;
        }
      `}</style>
    </Card>
  );
};

export default GenericApprovalWorkflow;
